import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBody from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import {
  useGetFeaturesPermissionsDistributable,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { SORTED_FIELD_PERMISSION, SORTED_MODULE_PERMISSION } from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { convertDualPermissions, convertPermissions } from '../../../utils/permission/permissionAdapter';
import { getFilteredPermissionData } from '../../../utils';
import {
  PermissionMatrixForm,
  useFilteredPermissionMatrix,
  PERMISSION_MODAL_TOOLTIP,
} from '../shared/permission-matrix';
import { type AccessProfileScope, ACCESS_PROFILE_CONFIG } from './types';
import InitialValuesForm from '../../../constants/initialValues';

interface AccessProfileFilterModalProps {
  id?: string;
  scope: AccessProfileScope;
  isModalOpen: boolean;
  closeModal: () => void;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  setFilterCustomValue: (value: string) => void;
}

const AccessProfileFilterModal = ({
  id,
  scope,
  isModalOpen,
  closeModal,
  handleRequest,
  setFilterCustomValue,
}: AccessProfileFilterModalProps): JSX.Element => {
  const config = ACCESS_PROFILE_CONFIG[scope];
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');

  const { handleRequest: handleRequestIdentity } = useWealthlaneIdentityApi();
  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(
    handleRequestIdentity,
    scope === 'FeatureSet'
  );
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest, scope === 'Role');

  const [moduleLevelPermissions, setModuleLevelPermissions] = useState<any[]>([]);
  const [fieldLevelPermissionsList, setFieldLevelPermissionsList] = useState<any[]>([]);

  useEffect(() => {
    if (scope === 'FeatureSet' && distributablePermissions?.fieldLevelPermissions) {
      setModuleLevelPermissions(distributablePermissions?.permissions ?? []);
      setFieldLevelPermissionsList(distributablePermissions?.fieldLevelPermissions ?? []);
    }
  }, [distributablePermissions, scope]);

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } =
    scope === 'FeatureSet'
      ? (() => {
        const moduleGroups = getFilteredPermissionData(
          convertDualPermissions(moduleLevelPermissions),
          'CustomerService.Customer.',
          allFieldValues
        );
        const fieldGroups = getFilteredPermissionData(
          convertDualPermissions(fieldLevelPermissionsList),
          'CustomerService.Customer.',
          allFieldValues
        );
        return {
          nonFieldLevelPermissionsGroups: moduleGroups.nonFieldLevelPermissionsGroups,
          fieldLevelPermissionsGroups: fieldGroups.fieldLevelPermissionsGroups,
        };
      })()
      : (() => {
        const result = getFilteredPermissionData(
          convertPermissions(newPermissions ?? []),
          'CustomerService.Customer.',
          allFieldValues
        );
        const filteredFieldGroups: Record<string, any[]> = {};
        Object.entries(result.fieldLevelPermissionsGroups).forEach(([groupKey, items]: [string, any]) => {
          const customerOnly = items.filter(
            (item: any) => !item.key.startsWith('CustomerService.Vendor.')
          );
          if (customerOnly.length > 0) {
            filteredFieldGroups[groupKey] = customerOnly;
          }
        });
        return {
          nonFieldLevelPermissionsGroups: result.nonFieldLevelPermissionsGroups,
          fieldLevelPermissionsGroups: filteredFieldGroups,
        };
      })();

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );

  const { filteredData, filteredFieldLevelData } = useFilteredPermissionMatrix({
    filterNonFieldLevelPermission,
    filterFieldLevelPermission,
    manualSearch,
    allFieldValues,
  });

  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  const handleSubmit = (values: any): void => {
    const permissions = Object.keys(values?.permissions)
      .filter((x) => values.permissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));
    const fieldLevelPermissions = Object.keys(values?.fieldLevelPermissions ?? {})
      .filter((x) => values.fieldLevelPermissions[x] !== false)
      .map((item) => item.replaceAll('-', '.'));

    setFilterCustomValue(permissions.concat(fieldLevelPermissions) as any);
    closeModal();
  };

  const initialFormData =
    scope === 'FeatureSet' ? InitialValuesForm.featureSetModal : InitialValuesForm.addSystemRoleForm;

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormData}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            size='lg'
            title={`Filter ${config.title}`}
            valueEdited={_.isEqual(values, initialFormData)}
            id={id}
            showToolTip={true}
            tooltipText={PERMISSION_MODAL_TOOLTIP}
            children={
              <Form id={`filter${scope}Form`}>
                <ModalBody id={`filter${scope}ModalBody`}>
                  <PermissionMatrixForm
                    variant='single'
                    modalStatus='Edit'
                    values={values}
                    errors={errors}
                    touched={touched}
                    setFieldValue={setFieldValue}
                    allFieldValues={allFieldValues}
                    filteredData={filteredData}
                    filteredFieldLevelData={filteredFieldLevelData}
                    nonFieldLevelPermissionsGroups={nonFieldLevelPermissionsGroups}
                    fieldLevelPermissionsGroups={fieldLevelPermissionsGroups}
                    filterNonFieldLevelPermission={filterNonFieldLevelPermission}
                    filterFieldLevelPermission={filterFieldLevelPermission}
                    manualSearch={manualSearch}
                    onSearchChange={setManualSearch}
                    searchInputId={`search${scope}FilterInput`}
                    handleCheckedAll={handleCheckedAll}
                  />
                </ModalBody>
                <ModalFooterComponent
                  id={`filter${scope}ModalFooter`}
                  modalStatus='Edit'
                  buttonTitle='Filter'
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onClickButton={() => {
                    handleSubmit(values);
                  }}
                  isEditShow
                  onEditModal={() => { }}
                  urlName={config.urlName}
                  valueChanged={_.isEqual(values, initialFormData)}
                  loading={false}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default AccessProfileFilterModal;
